import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnswerOptions } from '@/components/rocket-reader/AnswerOptions';
import { FuelBar } from '@/components/rocket-reader/FuelBar';
import { PlanetProgress } from '@/components/rocket-reader/PlanetProgress';
import { ReadingCard } from '@/components/rocket-reader/ReadingCard';
import { RewardToast } from '@/components/rocket-reader/RewardToast';
import { RocketScene, type RocketLaunchState } from '@/components/rocket-reader/RocketScene';
import { SparkleEffect } from '@/components/rocket-reader/SparkleEffect';
import {
  ROCKET_READER_PLANETS,
  firstChallengeIndexForPlanet,
  getNextChallengeIndexForPlanet,
  rocketReaderChallenges,
} from '@/data/rocketReaderChallenges';
import {
  clearRocketReaderProgress,
  createInitialRocketReaderProgress,
  loadRocketReaderProgress,
  saveRocketReaderProgress,
  type RocketReaderProgress,
} from '@/utils/rocketReaderStorage';
import '@/components/rocket-reader/rocket-reader.css';

type Feedback = {
  kind: 'happy' | 'try' | 'launch';
  message: string;
};

const LAUNCH_CHARGE_MS = 500;
const LAUNCH_TRAVEL_MS = 2200;
const LAUNCH_SETTLE_MS = 3900;

export function RocketReaderPage() {
  const [progress, setProgress] = useState<RocketReaderProgress>(() =>
    loadRocketReaderProgress(),
  );
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [hasAnsweredCorrect, setHasAnsweredCorrect] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [sparkleKey, setSparkleKey] = useState(0);
  const [launchState, setLaunchState] = useState<RocketLaunchState>('idle');
  const launchTimers = useRef<number[]>([]);

  const currentPlanet = ROCKET_READER_PLANETS[progress.currentPlanetIndex];
  const challenge = rocketReaderChallenges[progress.currentChallengeIndex] ?? rocketReaderChallenges[0];
  const speechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const helperMessage = useMemo(() => {
    if (feedback) return feedback.message;
    if (launchState !== 'idle') return 'Hold tight. The rocket is lifting off.';
    return 'Scout says: read slowly, then choose the answer that matches the story.';
  }, [feedback, launchState]);

  useEffect(() => {
    document.title = 'Rocket Reader | Alhudah';
  }, []);

  useEffect(() => {
    saveRocketReaderProgress(progress);
  }, [progress]);

  useEffect(() => {
    return () => {
      clearLaunchTimers();
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  function clearLaunchTimers() {
    launchTimers.current.forEach((timerId) => window.clearTimeout(timerId));
    launchTimers.current = [];
  }

  function scheduleLaunch(progressAtLaunch: RocketReaderProgress) {
    clearLaunchTimers();
    setLaunchState('charging');

    launchTimers.current.push(
      window.setTimeout(() => {
        setLaunchState('launching');
      }, LAUNCH_CHARGE_MS),
    );

    launchTimers.current.push(
      window.setTimeout(() => {
        const nextPlanetIndex = Math.min(
          progressAtLaunch.currentPlanetIndex + 1,
          ROCKET_READER_PLANETS.length - 1,
        );
        const nextPlanet = ROCKET_READER_PLANETS[nextPlanetIndex];
        const staysOnFinalPlanet = nextPlanetIndex === progressAtLaunch.currentPlanetIndex;
        const nextChallengeIndex = staysOnFinalPlanet
          ? getNextChallengeIndexForPlanet(
              progressAtLaunch.currentChallengeIndex,
              nextPlanet.name,
            )
          : firstChallengeIndexForPlanet(nextPlanet.name);

        setProgress({
          ...progressAtLaunch,
          currentChallengeIndex: nextChallengeIndex,
          currentPlanetIndex: nextPlanetIndex,
          fuel: 0,
          completedLaunches: progressAtLaunch.completedLaunches + 1,
        });
        setSelectedAnswer(null);
        setHasAnsweredCorrect(false);
        setSparkleKey((key) => key + 1);
        setFeedback({
          kind: 'launch',
          message: staysOnFinalPlanet
            ? 'Amazing orbit! Galaxy Garden is glowing brighter.'
            : `Welcome to ${nextPlanet.name}! ${nextPlanet.unlockText}`,
        });
        setLaunchState('landed');
      }, LAUNCH_TRAVEL_MS),
    );

    launchTimers.current.push(
      window.setTimeout(() => {
        setLaunchState('idle');
      }, LAUNCH_SETTLE_MS),
    );
  }

  function handleAnswer(answer: string) {
    if (hasAnsweredCorrect || launchState !== 'idle') return;

    setSelectedAnswer(answer);

    if (answer !== challenge.correctAnswer) {
      setFeedback({
        kind: 'try',
        message: 'Good try! Read it again and choose another answer.',
      });
      return;
    }

    const nextFuel = Math.min(100, progress.fuel + challenge.rewardFuel);
    const nextProgress = {
      ...progress,
      fuel: nextFuel,
      stars: progress.stars + challenge.rewardStars,
      answeredCorrectCount: progress.answeredCorrectCount + 1,
    };

    setProgress(nextProgress);
    setHasAnsweredCorrect(true);
    setSparkleKey((key) => key + 1);
    setFeedback({
      kind: 'happy',
      message: `Correct! +${challenge.rewardFuel} fuel and +${challenge.rewardStars} stars.`,
    });

    if (nextFuel >= 100) {
      scheduleLaunch(nextProgress);
    }
  }

  function handleNextQuestion() {
    if (!hasAnsweredCorrect || launchState !== 'idle' || progress.fuel >= 100) return;

    setProgress((current) => {
      const planet = ROCKET_READER_PLANETS[current.currentPlanetIndex];

      return {
        ...current,
        currentChallengeIndex: getNextChallengeIndexForPlanet(
          current.currentChallengeIndex,
          planet.name,
        ),
      };
    });
    setSelectedAnswer(null);
    setHasAnsweredCorrect(false);
    setFeedback(null);
  }

  function handleRestart() {
    if (!window.confirm('Restart Rocket Reader progress?')) return;

    clearLaunchTimers();
    clearRocketReaderProgress();
    setProgress(createInitialRocketReaderProgress());
    setSelectedAnswer(null);
    setHasAnsweredCorrect(false);
    setFeedback({
      kind: 'try',
      message: 'Progress restarted. The rocket is ready at Moon Meadow.',
    });
    setSparkleKey(0);
    setLaunchState('idle');
  }

  function handleReadAloud() {
    if (!speechSupported) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(challenge.readingText);
    utterance.lang = 'en-US';
    utterance.rate = 0.86;
    utterance.pitch = 1.08;
    window.speechSynthesis.speak(utterance);
  }

  const nextButtonLabel = launchState !== 'idle'
    ? 'Rocket launching'
    : hasAnsweredCorrect
      ? 'Next Question'
      : 'Choose an answer';

  return (
    <main className="rr-screen">
      <div className="rr-space-layer" aria-hidden="true" />
      <SparkleEffect activeKey={sparkleKey} />
      <RewardToast message={feedback?.message ?? null} tone={feedback?.kind ?? 'happy'} />

      <header className="rr-header">
        <Link className="rr-brand" to="/" aria-label="Back to Alhudah home">
          Alhudah
        </Link>
        <div className="rr-title-block">
          <p>Read. Answer. Launch!</p>
          <h1>Rocket Reader</h1>
        </div>
        <div className="rr-stars" aria-label={`${progress.stars} stars earned`}>
          <span aria-hidden="true">Stars</span>
          <strong>{progress.stars}</strong>
        </div>
      </header>

      <div className="rr-layout">
        <div className="rr-left-column">
          <RocketScene
            planet={currentPlanet}
            launchState={launchState}
            completedLaunches={progress.completedLaunches}
          />
          <FuelBar fuel={progress.fuel} isLaunching={launchState !== 'idle'} />
          <PlanetProgress
            planets={ROCKET_READER_PLANETS}
            currentPlanetIndex={progress.currentPlanetIndex}
          />
        </div>

        <div className="rr-play-column">
          <section className="rr-helper-card" aria-live="polite">
            <div className="rr-mascot" aria-hidden="true">RR</div>
            <p>{helperMessage}</p>
          </section>

          <ReadingCard
            challenge={challenge}
            onReadAloud={handleReadAloud}
            speechSupported={speechSupported}
          />

          <AnswerOptions
            options={challenge.options}
            correctAnswer={challenge.correctAnswer}
            selectedAnswer={selectedAnswer}
            hasAnsweredCorrect={hasAnsweredCorrect}
            disabled={launchState !== 'idle'}
            onSelect={handleAnswer}
          />

          <div className="rr-actions">
            <button
              type="button"
              className="rr-primary-button"
              onClick={handleNextQuestion}
              disabled={!hasAnsweredCorrect || launchState !== 'idle' || progress.fuel >= 100}
            >
              {nextButtonLabel}
            </button>
            <button type="button" className="rr-secondary-button" onClick={handleRestart}>
              Restart Progress
            </button>
          </div>

          <section className="rr-summary-card" aria-label="Progress summary">
            <div>
              <span>Correct answers</span>
              <strong>{progress.answeredCorrectCount}</strong>
            </div>
            <div>
              <span>Launches</span>
              <strong>{progress.completedLaunches}</strong>
            </div>
            <div>
              <span>Planet</span>
              <strong>{currentPlanet.name}</strong>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
